import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Inbox,
  Sparkles,
  Calendar,
  LogOut,
  Search,
  Trash2,
  ArrowUpRight,
  Phone,
  Mail,
  Building,
  Users,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LogoLockup } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";
import { CountUp } from "@/components/motion-primitives";

const STATUS_STYLES = {
  new: "bg-lime-pop text-ink border-ink",
  in_progress: "bg-coral text-ink border-ink",
  contacted: "bg-white text-ink border-ink",
  closed: "bg-ink text-white border-ink",
};

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, this_week: 0 });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/inquiries"),
        api.get("/inquiries/stats"),
      ]);
      setInquiries(list.data);
      setStats(st.data);
    } catch (err) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filtered = useMemo(() => {
    return inquiries.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        i.full_name?.toLowerCase().includes(q) ||
        i.company?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.phone?.toLowerCase().includes(q)
      );
    });
  }, [inquiries, query, statusFilter]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await api.patch(`/inquiries/${id}`, { status: newStatus });
      setInquiries((arr) => arr.map((x) => (x.id === id ? res.data : x)));
      if (selected?.id === id) setSelected(res.data);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      const st = await api.get("/inquiries/stats");
      setStats(st.data);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((arr) => arr.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted");
      const st = await api.get("/inquiries/stats");
      setStats(st.data);
    } catch (err) {
      toast.error("Failed to delete inquiry");
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard-page">
      {/* TOP BAR */}
      <header className="border-b border-ink bg-bone sticky top-0 z-40 glass-light">
        <div className="container-x flex items-center justify-between h-16">
          <LogoLockup />
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-warm">
              <span className="h-1.5 w-1.5 rounded-full bg-lime-pop" />
              Signed in · <span className="text-ink">{user?.username}</span>
            </div>
            <Button
              onClick={handleLogout}
              className="rounded-full bg-ink text-white hover:bg-ink-2 h-10 px-5"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container-x py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between flex-wrap gap-4"
        >
          <div>
            <div className="text-overline">Admin</div>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-ink tracking-[-0.03em] leading-[0.95]">
              Inquiries.
            </h1>
            <p className="mt-3 text-ink-2 max-w-lg">
              Review, triage, and respond to leads from the marketing site.
            </p>
          </div>
        </motion.div>

        {/* STATS */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={Inbox}
            label="Total inquiries"
            value={stats.total}
            tint="bg-white"
            testid="stat-total"
          />
          <StatCard
            icon={Sparkles}
            label="New · unworked"
            value={stats.new}
            tint="bg-lime-pop"
            testid="stat-new"
          />
          <StatCard
            icon={Calendar}
            label="Last 7 days"
            value={stats.this_week}
            tint="bg-coral"
            testid="stat-week"
          />
        </div>

        {/* FILTERS */}
        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-warm" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, company, email, phone..."
              className="pl-10 bg-white border-ink text-ink focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-0 h-11 rounded-xl"
              data-testid="dashboard-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[180px] bg-white border-ink text-ink h-11 rounded-xl"
              data-testid="dashboard-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* TABLE */}
        <div className="mt-6 rounded-3xl border border-ink bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-ink hover:bg-transparent bg-bone-2">
                <TableHead className="text-ink font-semibold">Date</TableHead>
                <TableHead className="text-ink font-semibold">Company</TableHead>
                <TableHead className="text-ink font-semibold">Contact</TableHead>
                <TableHead className="text-ink font-semibold hidden md:table-cell">
                  Employees
                </TableHead>
                <TableHead className="text-ink font-semibold hidden lg:table-cell">
                  Features
                </TableHead>
                <TableHead className="text-ink font-semibold">Status</TableHead>
                <TableHead className="text-ink font-semibold text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-ink">
                  <TableCell colSpan={7} className="text-center text-muted-warm py-12">
                    Loading inquiries...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow className="border-ink">
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-warm py-16"
                    data-testid="dashboard-empty-state"
                  >
                    {inquiries.length === 0
                      ? "No inquiries yet. Submissions from the contact form will appear here."
                      : "No inquiries match the current filters."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => setSelected(row)}
                    className="border-ink hover:bg-bone-2 cursor-pointer"
                    data-testid={`inquiry-row-${row.id}`}
                  >
                    <TableCell className="font-mono text-xs text-muted-warm whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell className="text-ink font-medium">
                      {row.company}
                    </TableCell>
                    <TableCell>
                      <div className="text-ink text-sm">{row.full_name}</div>
                      <div className="text-xs text-muted-warm">{row.email}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-ink-2 text-sm">
                      {row.employees}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1 max-w-[280px]">
                        {(row.features_needed || []).slice(0, 2).map((f) => (
                          <Badge
                            key={f}
                            className="bg-bone-2 text-ink border border-ink font-normal"
                          >
                            {f}
                          </Badge>
                        ))}
                        {(row.features_needed || []).length > 2 && (
                          <Badge className="bg-transparent text-muted-warm border border-ink/30 font-normal">
                            +{row.features_needed.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_STYLES[row.status]} border font-medium rounded-full`}>
                        {STATUS_LABELS[row.status] || row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <ArrowUpRight className="h-4 w-4 text-ink inline" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* DETAIL DIALOG */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-bone border-ink text-ink max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          {selected && (
            <>
              <DialogHeader>
                <div className="text-overline mb-2">Inquiry detail</div>
                <DialogTitle className="font-display text-4xl tracking-tight text-ink">
                  {selected.company}
                </DialogTitle>
                <p className="text-sm text-muted-warm font-mono">
                  {formatDate(selected.created_at)}
                </p>
              </DialogHeader>

              <div className="mt-4 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailRow icon={Users} label="Contact" value={selected.full_name} />
                  <DetailRow icon={Building} label="Company" value={selected.company} />
                  <DetailRow icon={Mail} label="Email">
                    <a href={`mailto:${selected.email}`} className="text-coral hover:underline break-all">
                      {selected.email}
                    </a>
                  </DetailRow>
                  <DetailRow icon={Phone} label="Phone">
                    <a href={`tel:${selected.phone}`} className="text-coral hover:underline">
                      {selected.phone}
                    </a>
                  </DetailRow>
                  <DetailRow icon={Users} label="Employees" value={selected.employees} />
                  <DetailRow icon={Clock} label="Contact window" value={selected.preferred_contact_time} />
                </div>

                {selected.current_provider && (
                  <DetailRow label="Current provider" value={selected.current_provider} />
                )}

                <div>
                  <div className="text-overline mb-2">Features needed</div>
                  {(selected.features_needed || []).length === 0 ? (
                    <p className="text-sm text-muted-warm">None specified</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selected.features_needed.map((f) => (
                        <Badge
                          key={f}
                          className="bg-lime-pop text-ink border border-ink font-medium rounded-full"
                        >
                          {f}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {selected.message && (
                  <div>
                    <div className="text-overline mb-2">Message</div>
                    <p className="text-sm text-ink-2 whitespace-pre-wrap leading-relaxed bg-white rounded-xl p-4 border border-ink">
                      {selected.message}
                    </p>
                  </div>
                )}

                <div>
                  <div className="text-overline mb-2">Status</div>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateStatus(selected.id, v)}
                  >
                    <SelectTrigger
                      className="bg-white border-ink text-ink rounded-xl"
                      data-testid="detail-status-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter className="mt-6 gap-2">
                <Button
                  onClick={() => handleDelete(selected.id)}
                  className="border border-ink bg-transparent text-ink hover:bg-coral hover:text-ink hover:border-ink rounded-full"
                  data-testid="detail-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button
                  onClick={() => setSelected(null)}
                  className="btn-lime rounded-full"
                  data-testid="detail-close-btn"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint = "bg-white", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4 }}
      className={`${tint} rounded-3xl border border-ink p-6 sm:p-7`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">
          {label}
        </div>
        <Icon className="h-4 w-4 text-ink" />
      </div>
      <div className="mt-4 font-display text-5xl sm:text-6xl text-ink leading-none tracking-tight">
        <CountUp value={value} />
      </div>
    </motion.div>
  );
}

function DetailRow({ icon: Icon, label, value, children }) {
  return (
    <div>
      <div className="text-overline mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="text-sm text-ink">
        {children || value || <span className="text-muted-warm">—</span>}
      </div>
    </div>
  );
}
